<template>
  <ListPageShell>
    <template #header>
      <div class="list-page-panel is-header-minimal">
        <div class="list-page-heading">
          <div class="list-page-heading-main">
            <h1 class="list-page-title">{{ uiText.pageTitle }}</h1>
            <p class="list-page-description">{{ uiText.pageDescription }}</p>
          </div>
        </div>
      </div>
    </template>

    <template #filters>
      <PageFilterBar
        body-class="p-6"
        content-class="flex flex-col gap-3 lg:flex-row lg:flex-nowrap lg:items-center lg:justify-between"
        show-refresh
        :refresh-loading="loading"
        @refresh="loadData"
      >
      <div class="flex flex-wrap items-center gap-3">
          <div class="relative min-w-[180px] flex-1">
            <input
              v-model="searchForm.keyword"
              type="text"
              :placeholder="uiText.searchPlaceholder"
              class="form-search-input"
              @keyup.enter="handleSearch"
            >
            <svg class="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
          </div>

          <el-select
            v-model="searchForm.status"
            :placeholder="uiText.allStatus"
            clearable
            class="form-select form-control-sm w-28"
            @change="handleSearch"
          >
            <el-option
              v-for="tab in statusOptions"
              :key="tab.value"
              :label="tab.label"
              :value="tab.value"
            />
          </el-select>

          <el-select
            v-model="searchForm.customerId"
            :placeholder="uiText.selectCustomer"
            filterable
            remote
            reserve-keyword
            clearable
            class="form-select form-control-sm w-44"
            popper-class="customer-select-dropdown"
            :remote-method="handleCustomerSearch"
            :loading="customerLoading"
            @visible-change="handleCustomerDropdownVisible"
            @change="handleSearch"
          >
            <el-option
              v-for="customer in customerList"
              :key="customer.id"
              :label="customer.name"
              :value="customer.id"
            >
              <div class="flex items-center justify-between">
                <span>{{ customer.name }}</span>
                <span v-if="customer.phone" class="ml-2 text-xs text-slate-400">{{ customer.phone }}</span>
              </div>
            </el-option>

            <template v-if="customerPagination.total > customerList.length" #footer>
              <div class="border-t border-slate-200 py-2 text-center">
                <button
                  type="button"
                  class="text-sm text-primary-600 hover:text-primary-700 disabled:opacity-50"
                  :disabled="customerLoading"
                  @click.stop="loadMoreCustomers"
                >
                  {{ customerLoading ? uiText.loadingMore : `${uiText.loadMore} (${customerList.length}/${customerPagination.total})` }}
                </button>
              </div>
            </template>
          </el-select>
      </div>

      <template #actions>
        <button
          v-permission="'plan:save'"
          class="flex h-10 items-center gap-2 whitespace-nowrap rounded-lg bg-primary-600 px-5 text-white transition-colors hover:bg-primary-700"
          @click="openCreateModal"
        >
          <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
          </svg>
          {{ uiText.createProposal }}
        </button>
      </template>

      <template #searchActions>
        <button
          type="button"
          class="btn-primary"
          :disabled="loading"
          @click="handleSearch"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
          搜索
        </button>
        <button
          type="button"
          class="btn-secondary"
          :disabled="loading"
          @click="resetSearch"
        >
          重置
        </button>
      </template>

      <template #extra>
        <div class="border-t border-slate-200 pt-4">
          <div class="flex flex-wrap gap-2">
          <button
            v-for="tab in tabs"
            :key="tab.value"
            :class="[
              'rounded-full px-3 py-1.5 text-sm font-medium transition-colors',
              searchForm.status === tab.value
                ? 'bg-primary-100 text-primary-700'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            ]"
            @click="searchForm.status = tab.value; handleSearch()"
          >
            {{ tab.label }}
          </button>
          </div>
        </div>
      </template>
      </PageFilterBar>
    </template>

    <div class="list-page-table-panel">
      <ProposalListTable
        :plans="planList"
        :loading="loading"
        :pagination="pagination"
        :empty-description="uiText.emptyDescription"
        @detail="openDetailModal"
        @edit="openEditModal"
        @approval-detail="openApprovalDetailModal"
        @customer-click="openCustomerDetailFromPlan"
        @page-change="handlePageChange"
        @size-change="handlePageSizeChange"
      />
    </div>

    <ProposalFormModal
      v-model:visible="showEditModal"
      :isEdit="isEdit"
      :editData="editPlanData"
      @success="loadData"
    />

    <PlanDetailModal
      v-model:visible="showDetailModal"
      :planData="planDetail"
    />

    <ApprovalDetailModal
      v-model="showApprovalDetailModal"
      :instance-id="selectedApprovalInstanceId"
      type="proposal"
      :title="uiText.approvalDetail"
    />

    <CustomerDetailModal
      v-model="showCustomerDetailModal"
      :customer="customerDetailData"
      :visible-tabs="['basic', 'proposals', 'orders']"
    />
  </ListPageShell>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { ElMessage } from 'element-plus'
import { getPlanPage, getPlanDetail } from '@/api/plan'
import { getCustomerPage } from '@/api/customer'
import ApprovalDetailModal from '@/components/ApprovalDetailModal.vue'
import CustomerDetailModal from '@/components/CustomerDetailModal.vue'
import ListPageShell from '@/components/ListPageShell.vue'
import PageFilterBar from '@/components/PageFilterBar.vue'
import PlanDetailModal from '@/components/PlanDetailModal.vue'
import ProposalFormModal from '@/components/ProposalFormModal.vue'
import ProposalListTable from '@/components/ProposalListTable.vue'

const uiText = {
  pageTitle: "方案管理",
  pageDescription: "管理销售报价方案，包括创建、审批、跟踪等功能。",
  searchPlaceholder: "搜索方案编号、名称",
  allStatus: "全部状态",
  selectCustomer: "选择客户",
  loadingMore: "加载中...",
  loadMore: "加载更多",
  createProposal: "新建方案",
  emptyDescription: "您可以创建一个新的报价方案来开始。",
  approvalDetail: "审批详情",
  customerListError: "获取客户列表失败",
  proposalListError: "加载方案列表失败",
  proposalDetailError: "获取方案详情失败",
  approvalMissing: "该方案暂无审批实例"
}

const tabs = [
  { label: '\u5168\u90e8', value: '' },
  { label: '\u5f85\u5ba1\u6279', value: 4 },
  { label: '\u6267\u884c\u4e2d', value: 1 },
  { label: '\u5df2\u5b8c\u6210', value: 2 },
  { label: '\u8349\u7a3f', value: 'draft' },
  { label: '\u5df2\u53d6\u6d88', value: 3 }
]

const statusOptions = [...tabs]

const route = useRoute()

const showEditModal = ref(false)
const showDetailModal = ref(false)
const showCustomerDetailModal = ref(false)
const showApprovalDetailModal = ref(false)
const selectedApprovalInstanceId = ref(null)
const isEdit = ref(false)
const editPlanData = ref(null)
const planDetail = ref(null)
const customerDetailData = ref(null)
const loading = ref(false)

const searchForm = reactive({
  keyword: '',
  customerId: '',
  status: ''
})

const pagination = reactive({
  current: 1,
  size: 20,
  total: 0,
  pages: 0
})

const planList = ref([])

const customerList = ref([])
const customerLoading = ref(false)
const customerSearchKeyword = ref('')
const customerPagination = reactive({
  page: 1,
  size: 20,
  total: 0
})

const fetchCustomers = async (keyword = '', isLoadMore = false) => {
  if (customerLoading.value) return

  customerLoading.value = true

  try {
    const result = await getCustomerPage({
      page: isLoadMore ? customerPagination.page + 1 : 1,
      size: customerPagination.size,
      name: keyword || undefined
    })

    if (isLoadMore) {
      customerList.value = [...customerList.value, ...(result?.records || [])]
      customerPagination.page = result?.current || customerPagination.page + 1
    } else {
      customerList.value = result?.records || []
      customerPagination.page = result?.current || 1
    }

    customerPagination.total = result?.total || 0
  } catch (error) {
    console.error('fetchCustomers failed:', error)
    ElMessage.error(uiText.customerListError)
  } finally {
    customerLoading.value = false
  }
}

let customerSearchTimer = null

const handleCustomerSearch = (query) => {
  customerSearchKeyword.value = query

  if (customerSearchTimer) {
    clearTimeout(customerSearchTimer)
  }

  customerSearchTimer = setTimeout(() => {
    fetchCustomers(query, false)
  }, 300)
}

const handleCustomerDropdownVisible = (visible) => {
  if (visible && customerList.value.length === 0) {
    fetchCustomers('', false)
  }
}

const loadMoreCustomers = () => {
  if (!customerLoading.value) {
    fetchCustomers(customerSearchKeyword.value, true)
  }
}

const loadData = async () => {
  loading.value = true

  try {
    const params = {
      keyword: searchForm.keyword || undefined,
      customerId: searchForm.customerId || undefined,
      page: pagination.current,
      size: pagination.size
    }

    if (searchForm.status === 'draft') {
      params.isDraft = 1
    } else if (searchForm.status !== '') {
      params.status = searchForm.status
    }

    const data = await getPlanPage(params)
    planList.value = data?.records || []
    pagination.current = data?.current || 1
    pagination.size = data?.size || pagination.size
    pagination.total = data?.total || 0
    pagination.pages = data?.pages || 0
  } catch (error) {
    ElMessage.error(uiText.proposalListError)
  } finally {
    loading.value = false
  }
}

const handleSearch = () => {
  pagination.current = 1
  loadData()
}

const resetSearch = () => {
  searchForm.keyword = ''
  searchForm.customerId = ''
  searchForm.status = ''
  pagination.current = 1
  loadData()
}

const handlePageChange = (page) => {
  if (page < 1 || page > pagination.pages) return
  pagination.current = page
  loadData()
}

const handlePageSizeChange = (size) => {
  pagination.size = size
  pagination.current = 1
  loadData()
}

const openCreateModal = () => {
  isEdit.value = false
  editPlanData.value = null
  showEditModal.value = true
}

const openEditModal = (plan) => {
  isEdit.value = true
  editPlanData.value = plan
  showEditModal.value = true
}

const openDetailModal = (plan) => {
  planDetail.value = plan
  showDetailModal.value = true
}

const openCustomerDetailModal = (customer) => {
  if (!customer?.id) return
  customerDetailData.value = { id: customer.id, name: customer.name || '' }
  showCustomerDetailModal.value = true
}

const openCustomerDetailFromPlan = (plan) => {
  if (!plan?.customerId) return
  openCustomerDetailModal({ id: plan.customerId, name: plan.customerName })
}

const openApprovalDetailModal = (plan) => {
  if (!plan?.approvalInstanceId) {
    ElMessage.warning(uiText.approvalMissing)
    return
  }

  selectedApprovalInstanceId.value = plan.approvalInstanceId
  showApprovalDetailModal.value = true
}

onMounted(async () => {
  const { id, planId, keyword, customerId, status, create, openDetail } = route.query
  const targetPlanId = id || planId

  if (targetPlanId && openDetail === 'true') {
    try {
      const detail = await getPlanDetail(targetPlanId)
      if (detail) {
        planDetail.value = detail
        showDetailModal.value = true
      }
    } catch (error) {
      console.error('getPlanDetail failed:', error)
      ElMessage.error(uiText.proposalDetailError)
    }

    window.history.replaceState({}, '', '/proposal')
  } else if (targetPlanId) {
    searchForm.keyword = targetPlanId
  }

  if (keyword) {
    searchForm.keyword = keyword
  }

  if (customerId) {
    searchForm.customerId = customerId
  }

  if (status !== undefined && status !== '') {
    searchForm.status = Number(status)
  }

  loadData()

  if (create === 'true') {
    openCreateModal()
    window.history.replaceState({}, '', '/proposal')
  }
})
</script>
